import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {Optional} from 'util/Types';

import {BLUE} from './Colors';

export interface IndeterminateCheckboxProps<T> {
  'aria-label'?: React.AriaAttributes['aria-label'];
  className?: Optional<string>;
  disabled?: boolean;
  items: Array<T>;
  /** A function to determine whether a checkable item is disabled */
  disabledMap?: (item: T) => boolean;
  /** A function to determine whether a checkable item is  checked */
  selectedMap: (item: T) => boolean;
  /** A function to be called when this checkbox is toggled */
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}

/**
 * A checkbox that can display indeterminate (partially checked) state. This component is intended to be used
 * at the top of a list of checkable items. It should render a regular checked state when all items are
 * checked, a regular unchecked state when all items are unchecked, and an indeterminate state when some items
 * are checked and some items are not.
 */
function IndeterminateCheckbox<T>({
  'aria-label': ariaLabel,
  className,
  disabled = false,
  items,
  disabledMap,
  selectedMap,
  onChange,
}: IndeterminateCheckboxProps<T>): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  // filter out disabled rows in order to enable checking all editable boxes
  const everySelected = items
    .filter((item) => disabledMap == null || !disabledMap(item))
    .every((item) => selectedMap(item));
  const someSelected = items
    .filter((item) => disabledMap == null || !disabledMap(item))
    .some((item) => selectedMap(item));
  const indeterminate = !everySelected && someSelected;

  useEffect(() => {
    if (ref.current != null) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      aria-label={ariaLabel}
      checked={someSelected}
      className={classNames(className, css(styles.checkbox))}
      disabled={disabled}
      type="checkbox"
      onChange={onChange}
      ref={ref}
      {...getTestProps({component: 'IndeterminateCheckbox', element: 'root'})}
    />
  );
}

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default IndeterminateCheckbox;
